import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { Box, Button, Card, CardContent, Collapse, Grid } from "@mui/material";
// React Icons (Font Awesome set)
import {
  FaClock,
  FaBell,
  FaTachometerAlt,
  FaChartBar,
  FaCogs,
  FaSlidersH,
} from "react-icons/fa";
// React Icons for Stats Section
import { FaHistory, FaBuilding, FaCalendarCheck } from "react-icons/fa";
import FeatureSection from "./FeatureSection";
import { motion } from "framer-motion";

// Inline CSS for Homepage to ensure icon colors
const styles = `
  .feature-icon {
    font-size: 64px !important;
    transition: color 0.3s ease, fill 0.3s ease !important;
  }
  .feature-icon.hovered {
    color: #006A71 !important;
    fill: #006A71 !important;
  }
  .feature-icon:not(.hovered) {
    color: #033538 !important;
    fill: #033538 !important;
  }
`;

const Homepage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const features = [
    {
      icon: "🧭",
      title: "Clarity Over Complexity",
      details:
        "Minimal learning curve. Designed to make interview management feel effortless.",
    },
    {
      icon: "🛠️",
      title: "Role-Based Dashboards",
      details:
        "Tailored interfaces for HR, Interviewers, and Admins with relevant tools at their fingertips.",
    },
    {
      icon: "📈",
      title: "Real-Time Progress & Analytics",
      details:
        "Track every candidate's journey and panel performance in one elegant view.",
    },
    {
      icon: "🧾",
      title: "Structured Feedback Made Easy",
      details:
        "Simple templates and reminders ensure timely, useful input from every round.",
    },
    {
      icon: "🔄",
      title: "Smooth Scheduling Workflow",
      details:
        "Integrated with calendars and notifications—no more back-and-forths.",
    },
    {
      icon: <FaSlidersH />,
      title: "Custom Workflows",
      details:
        "Configure workflows for screening, scoring, and follow-ups to match your hiring policies.",
    },
  ];

  // Testimonials Data
  const testimonials = [
    {
      quote:
        "Strategic Hire transformed our hiring process! The scheduling is seamless, and the insights are spot-on.",
      author: "Sarah Johnson",
      company: "TechCorp",
    },
    {
      quote:
        "The mobile dashboard saved us hours of work. Highly recommend this tool!",
      author: "Michael Lee",
      company: "Innovate Solutions",
    },
    {
      quote:
        "Custom workflows and integrations have made our recruitment smarter and faster.",
      author: "Emily Davis",
      company: "Global Enterprises",
    },
  ];

  // Pricing Data
  const pricingPlans = [
    {
      title: "Starter",
      price: "$99/mo",
      features: ["50 interviews/mo", "Basic AI analytics", "Email support"],
    },
    {
      title: "Pro",
      price: "$299/mo",
      features: [
        "200 interviews/mo",
        "Advanced AI insights",
        "Priority support",
      ],
    },
    {
      title: "Enterprise",
      price: "Custom",
      features: ["Unlimited interviews", "Full AI suite", "Dedicated support"],
    },
  ];

  return (
    <div style={{ backgroundColor: "white", minHeight: "100vh" }}>
      {/* Inline CSS */}
      <style>{styles}</style>
      {/* Hero Section */}
      <Box
        sx={{
          py: 8,
          background: "linear-gradient(135deg, #004d4d, #002626)",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Grid
          container
          spacing={6} // Increased spacing between grid items
          sx={{
            maxWidth: "1200px",
            width: "100%",
            mx: "auto",

            alignItems: "center",
          }}
        >
          {/* Left Side - Image */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <Box
                component="img"
                src="/assests/images/heroimage.png"
                alt="Strategic Hiring"
                sx={{
                  width: "100%",
                  maxWidth: "650px",
                  borderRadius: "12px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                }}
              />
            </motion.div>
          </Grid>

          {/* Right Side - Text Content */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              textAlign: { xs: "center", md: "left" },
              pl: { md: 3 }, // Add extra padding on the left side of text content
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* Animated Title */}
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Typography
                  variant="h3"
                  color="#e0f7fa"
                  sx={{
                    fontWeight: "900",
                    lineHeight: 1.1,
                    wordBreak: "break-word",
                  }}
                >
                  Revolutionize Hiring with Strategic Precision
                </Typography>
              </motion.div>

              {/* Animated Subtitle */}
              <motion.div
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
              >
                <Typography
                  variant="h5"
                  color="#b2dfdb"
                  sx={{
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  Empower your team with seamless recruitment — from talent
                  search to onboarding success.
                </Typography>
              </motion.div>

              {/* Button */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: 1,
                  scale: [1, 1.05, 1],
                  transition: {
                    opacity: { duration: 0.5 },
                    scale: { duration: 1.5, repeat: Infinity },
                  },
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                }}
              >
                <Button
                  sx={{
                    backgroundColor: "white",
                    color: "#004d4d",
                    py: 2,
                    px: 4,
                    "&:hover": { backgroundColor: "#b2dfdb", color: "#004d4d" },
                    borderRadius: 10,
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Start Your Hiring Revolution
                </Button>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <FeatureSection />
      {/* Stats Section */}
      <Box
        sx={{
          backgroundColor: "#006A71",
          color: "white",
          display: "flex",
          justifyContent: "space-evenly",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FaHistory style={{ fontSize: 80, color: "white" }} />
          <Typography>5+ years of experience</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FaBuilding style={{ fontSize: 80, color: "white" }} />
          <Typography>200+ Companies</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FaCalendarCheck style={{ fontSize: 80, color: "white" }} />
          <Typography>10K+ Interviews Processed</Typography>
        </Box>
      </Box>
      {/* Features Section */}
      <Box sx={{ py: 5 }}>
        <Typography
          variant="h5"
          color="#006A71"
          sx={{ textAlign: "center", p: 3, fontWeight: "bold" }}
        >
          What Strategic Hire Offers Enterprises
        </Typography>
        <Grid container spacing={3} sx={{ px: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    transition: "all 0.3s ease",
                  },
                  textAlign: "center",
                  position: "relative",
                  backgroundColor: "#eef0eb",
                }}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 5,
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <span
                      role="img"
                      aria-label="compass"
                      className={`feature-icon ${
                        hoveredCard === index ? "hovered" : ""
                      }`}
                      style={{
                        fontSize: 64,
                        transition: "color 0.3s ease, fill 0.3s ease",
                        color: hoveredCard === index ? "#7209b7" : "#000000",
                      }}
                    >
                      {feature.icon}
                    </span>
                  </Box>

                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: "bold", color: "#006A71" }}
                  >
                    {feature.title}
                  </Typography>
                  {/* <Typography variant="body1" color="#033538">
                    {feature.description}
                  </Typography> */}
                  <Collapse
                    in={hoveredCard === index}
                    timeout="auto"
                    unmountOnExit
                    sx={{ width: "100%", mt: 2 }}
                  >
                    <Box
                      sx={{ backgroundColor: "#f5f5f5", p: 2, borderRadius: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {feature.details}
                      </Typography>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ py: 5, backgroundColor: "#9ACBD0" }}>
        <Typography
          variant="h4"
          color="#006A71"
          sx={{ textAlign: "center", p: 3, fontWeight: "bold" }}
        >
          What Our Clients Say
        </Typography>
        <Grid container spacing={3} sx={{ px: 8 }}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card
                sx={{
                  textAlign: "center",
                  p: 2,
                  height: "100%", // Make all cards take full height of the grid item
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body1" color="#033538" sx={{ mb: 2 }}>
                    "{testimonial.quote}"
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", color: "#48A6A7" }}
                  >
                    {testimonial.author}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonial.company}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ py: 5 }}>
        <Typography
          variant="h5"
          color="#006A71"
          sx={{ textAlign: "center", p: 3, fontWeight: "bold" }}
        >
          Pricing Plans
        </Typography>
        <Grid container spacing={3} sx={{ px: 8 }}>
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card
                sx={{
                  textAlign: "center",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#006A71" }}
                  >
                    {plan.title}
                  </Typography>
                  <Typography variant="h4" color="#033538" sx={{ my: 2 }}>
                    {plan.price}
                  </Typography>
                  {plan.features.map((feature, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">
                      {feature}
                    </Typography>
                  ))}
                  <Button
                    sx={{
                      mt: 2,
                      backgroundColor: "#006A71",
                      color: "white",
                      "&:hover": { backgroundColor: "#033538" },
                    }}
                  >
                    Choose Plan
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );
};

export default Homepage;
